import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useQuery } from "@apollo/client";

import { ROUTES } from "@constants/routes.constants";
import { DATE_FORMATS } from "@constants/date-formats.constants";
import { useAppDispatch } from "@services/redux";
import { clearOrder, updateDraftOrder } from "@services/redux/order";
import { browserStorage, takeStash } from "../../services/group-order/group-order.utils";
import { GET_ONE_ORDER } from "../../Graphql/queries";

interface ICheckoutSuccessNavState {
  catererName: string;
  deliveryDate: string;
  deliveryTime: string;
  caterCashTotal: number;
  id: string;
  orderNo: string;
}

export function useCheckoutSuccessState() {
  const navigate = useNavigate();
  const location = useLocation() as { 
    state: ICheckoutSuccessNavState | null;
    search: string;
  };
  const dispatch = useAppDispatch();

  const [isBackButtonClicked, setBackButtonPress] = useState(false);
  const [orderState, setOrderState] = useState<ICheckoutSuccessNavState>({
    catererName: "",
    deliveryDate: "",
    deliveryTime: "",
    id: "",
    caterCashTotal: 0,
    orderNo: "",
  });

  // Get URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get("session_id");
  const orderId = queryParams.get("order_id");

  const didInit = useRef(false);
  // Identifies this completed checkout, so a reload of this page doesn't clear again.
  const completedKey = sessionId || orderId || location.state?.id || "";
  useEffect(() => {
    // StrictMode runs effects twice in dev; a second pass would wipe the restored cart.
    if (didInit.current) return;
    didInit.current = true;
    const flagKey = completedKey ? `cp.checkoutSuccess.cleared.${completedKey}` : "";
    try {
      if (flagKey && window.sessionStorage.getItem(flagKey)) return;
    } catch (e) {
      // sessionStorage unavailable: clear as before
    }
    // Clear any existing draft/edit cart state once the order is completed
    // This ensures the cart is empty when the user returns to the site
    dispatch(clearOrder());
    // MAIN-861: a group checkout put the rep's own cart aside; bring it back.
    const stash = takeStash(browserStorage());
    if (stash) dispatch(updateDraftOrder(stash));
    try {
      if (flagKey) window.sessionStorage.setItem(flagKey, "1");
    } catch (e) {
      // ignore
    }
  }, [dispatch, completedKey]);

  useEffect(() => {
    // If we have state from navigation, use it
    if (location.state) {
      setOrderState((s) => ({ ...s, ...location.state }));
    }
    // If we don't have state and no orderId in URL, redirect to home
    else if (!orderId) {
      console.log("No orderId in URL and no state, redirecting to home");
      navigate(ROUTES.root);
    }
    // If we have orderId, let the GraphQL query handle the data fetching
    else {
      console.log("OrderId found in URL:", orderId);
    }
  }, [location.state, orderId, navigate]);

  // Fetch order data if we have order_id from URL
  const { data: orderData, loading: orderLoading, error: orderError } = useQuery(GET_ONE_ORDER, {
    variables: { orderId },
    skip: !orderId || !!location.state, // Skip if no orderId or if we have state
    onCompleted: (data) => {
      console.log("Order data fetched successfully:", data);
      if (data?.getOneOrder) {
        const order = data.getOneOrder;
        setOrderState({
          catererName: order.catererDetails?.businessName || "",
          deliveryDate: order.deliveryDate || "",
          deliveryTime: order.deliveryTime || "",
          id: order._id || "",
          caterCashTotal: order.caterCashTotal || 0,
          orderNo: order.orderNo || "",
        });
      } else {
        console.log("No order data found in response");
      }
    },
    onError: (error) => {
      console.error("Error fetching order:", error);
      // Don't redirect to home on error, just show the success page with basic info
      // The user should still see the success page even if order details can't be loaded
    },
  });

  // Time zone is no longer needed as we're formatting the time with AM/PM directly

  const deliveryDate = useMemo(() => {
    return orderState.deliveryDate
      ? moment(orderState.deliveryDate).format(DATE_FORMATS.humanReadableDate)
      : "";
  }, [orderState.deliveryDate]);

  useEffect(() => {
    const onBackButtonEvent = (e) => {
      e.preventDefault();
      if (!isBackButtonClicked) {
        window.history.pushState(null, null, window.location.pathname);
        setBackButtonPress(false);
      }
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener("popstate", onBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, []);

  function onNewOrder() {
    // The mount effect already cleared the order and restored any stashed cart.
    navigate(ROUTES.root);
  }

  function onViewOrder() {
    const link = orderState.id ? `/orders/${orderState.id}` : "/orders";
    navigate(link);
  }

  function onAddToCalendar() {
    // Create calendar event data
    const eventTitle = orderState.orderNo 
      ? `Caterplace order #${orderState.orderNo} from ${orderState.catererName} arrives`
      : `Caterplace order from ${orderState.catererName} arrives`;
    const link = orderState.id ? `${window.location.origin}/orders/${orderState.id}` : `${window.location.origin}/orders`;
    const eventDescription = orderState.orderNo
      ? `Order #${orderState.orderNo} - View your order here: ${link}`
      : `View your order here: ${link}`;

    // Combine date and time for start and end times
    const startDate = orderState.deliveryDate && orderState.deliveryTime
      ? moment(orderState.deliveryDate).set({
          hour: Number(orderState.deliveryTime.split(":")[0]),
          minute: Number(orderState.deliveryTime.split(":")[1]),
          second: 0,
        })
      : null;

    // If we have a valid date, create the calendar event
    if (startDate && startDate.isValid()) {
      // End time is 1 hour after start time
      const endDate = startDate.clone().add(1, 'hour');

      // Format dates for calendar URL
      const startDateFormatted = startDate.format('YYYYMMDDTHHmmss');
      const endDateFormatted = endDate.format('YYYYMMDDTHHmmss');

      // Create Google Calendar URL
      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&details=${encodeURIComponent(eventDescription)}&dates=${startDateFormatted}/${endDateFormatted}`;

      // Open the calendar URL in a new tab
      window.open(googleCalendarUrl, '_blank');
    } else {
      // Handle case where date/time is invalid
      console.error('Invalid date or time for calendar event');
      alert('Unable to create calendar event due to invalid date or time information.');
    }
  }

  return {
    date: deliveryDate,
    time: orderState.deliveryTime ? moment(orderState.deliveryTime, "HH:mm").format(DATE_FORMATS.humanReadableTime) : "",
    catererName: orderState.catererName,
    caterCash: +orderState.caterCashTotal,
    orderNo: orderState.orderNo,
    onNewOrder,
    onViewOrder,
    onAddToCalendar,
    loading: orderLoading
  };
}
